import { z } from 'zod';
import { GRID_LED_STANDBY_PLANNING_MODEL_VERSION, GRID_LED_STANDBY_PLANNING_LIMITATIONS } from './gridLedStandbyPlanning';

const value = z.number().finite().nullable();
const check = z.enum(['pass', 'fail', 'indeterminate']);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable();

/** Deliberate public allowlist. Private snapshot identities, literal quotes,
 * operator reasons and field paths must never be copied into this object. */
export const gridLedStandbyPlanningReportProjectionSchema = z.object({
  schemaVersion: z.literal(1),
  assessmentKind: z.literal('grid-led-standby-planning'),
  modelVersion: z.literal(GRID_LED_STANDBY_PLANNING_MODEL_VERSION),
  authority: z.literal('user-declared-planning'),
  inputHash: z.string().regex(/^[a-f\d]{64}$/),
  resultHash: z.string().regex(/^[a-f\d]{64}$/),
  staticCapacityVerdict: check,
  engineeringQualification: z.literal('indeterminate'),
  phases: z.array(z.object({
    /** Ordinal position in the frozen assessment; never a private phase ID. */
    phase: z.number().int().positive().max(20),
    facilityMW: value, itMW: value, declaredGridImportMW: value,
    gridServiceDate: date, operationTargetDate: date,
    modeledNormalGridImportMarginMW: value,
    gridCapacityCheck: check, gridScheduleCheck: check,
    modeledStandbyUnitOutMarginMW: value, standbyCapacityCheck: check,
    idealUpsMinutes: value, upsPowerCheck: check, upsEnergyBasisCheck: check,
  }).strict()).min(1).max(20),
  standby: z.object({
    role: z.literal('emergency-standby-and-testing-only'), ratingBasis: z.literal('site-net'),
    unitCount: value, siteNetMWPerUnit: value, unavailableUnits: value,
    installedMW: value, unavailableMW: value, remainingMW: value,
  }).strict(),
  ups: z.object({ dischargeMW: value, usableEnergyMWh: value }).strict(),
  annual: z.object({
    itDesignMW: value, itUtilizationPct: value, pue: value, hoursPerYear: value,
    comparisonPue: value, gridTonnesCo2ePerMWh: value,
    testLoadPct: value, testHoursPerYear: value,
    testDuty: z.literal('load-bank-no-grid-displacement'),
    litresPerMWh: value, kgCo2ePerLitre: value,
    itMWh: value,
    normalGridEquivalentDemandMWh: value,
    comparisonGridEquivalentDemandMWh: value,
    gridImportsAfterDeclaredEmergencyMWh: value,
    emergencyEnergyBalanceCheck: check,
    standbyTestingMWh: value, standbyTestingLitres: value,
    normalGridEquivalentTonnesCo2e: value,
    gridImportsAfterDeclaredEmergencyTonnesCo2e: value,
    standbyTestingTonnesCo2e: value,
    normalGridAndTestingReferenceSubtotalTonnesCo2e: value,
    declaredEmergencyOutageEnergyMWh: value, emergencyOutageLitres: value,
    emergencyOutageTonnesCo2e: value,
    completeTotalIncludingDeclaredEmergencyTonnesCo2e: value,
    gridDisplacementFromTestingMWh: z.literal(0),
  }).strict(),
  sourceBasis: z.object({
    reviewedDocumentPlanningFields: z.number().int().nonnegative().max(300),
    operatorPlanningAssumptionFields: z.number().int().nonnegative().max(300),
    /** Frozen numbered rows; normal citation compaction rewrites these tokens. */
    citations: z.array(z.string().regex(/^\[Ref [1-9]\d*\]$/)).max(300),
  }).strict(),
  limitations: z.array(z.string()).length(GRID_LED_STANDBY_PLANNING_LIMITATIONS.length),
}).strict().superRefine((projection, ctx) => {
  if (projection.limitations.some((text, index) => text !== GRID_LED_STANDBY_PLANNING_LIMITATIONS[index])) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['limitations'], message: 'Planning limitations must retain the fixed versioned scope.' });
  }
  if (projection.phases.some((phase, index) => phase.phase !== index + 1)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phases'], message: 'Planning phases must use consecutive public ordinals.' });
  }
});

export type GridLedStandbyPlanningReportProjection = z.infer<typeof gridLedStandbyPlanningReportProjectionSchema>;
